import { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Navbar, Nav, Dropdown } from 'react-bootstrap'
import { Redirect } from 'react-router-dom'
import { Typeahead } from 'react-bootstrap-typeahead'
import layoutHelpers from './helpers'
import store from '../../store'
import '../../vendor/libs/react-bootstrap-typeahead/react-bootstrap-typeahead.scss'
import { getUserInfo, setDefaultWebsite, getDashboardConfiguration } from '../../api/ExternalApi'
import { getChatSignalRConnection } from '../../api/ChatSignalR'
import { getNotificationSignalRConnection } from '../../api/NotificationSignalR'
import { fetchConversationSummary, getSupportAvailability, setSupportAvailability } from '../../api/ChatConversationsApi'
import { getDomainInfo } from '../../utils/userAccess'
import { hasAnyRole, showToastMessages } from '../../utils'
import { initChatSound, initChatDesktopNotifications, notifyIncomingChat } from '../../utils/chatAlerts'
import { withTranslation } from 'react-i18next'
import { NotificationBell } from '../../components/notifications/NotificationCenter'

// Alert preference for someone who is NOT on any operator roster — an Administrator covering support, say.
// They still receive every chat alert (the sidenav and the navbar gate on ROLES, not the roster), so they
// need the same off switch; there is simply no server-side ChatAvailable row to put it in.
const SUPPORT_AVAILABLE_KEY = 'cbs.supportAvailable'

function readLocalSupportAvailable() {
  try {
    return window.localStorage.getItem(SUPPORT_AVAILABLE_KEY) !== 'false'
  } catch {
    return true // private mode or blocked storage — alerts on is the safe default
  }
}

function writeLocalSupportAvailable(available) {
  try {
    window.localStorage.setItem(SUPPORT_AVAILABLE_KEY, available ? 'true' : 'false')
  } catch {
    /* preference simply does not persist; the in-memory state still applies for this session */
  }
}

function escapeToastText(value) {
  return String(value || '').replace(/[&<>"']/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  }[character]))
}

class LayoutNavbar extends Component {
  constructor(props) {
    super(props)

    this.isRTL = document.documentElement.getAttribute('dir') === 'rtl'
    var state = store.getState();
    var selectedWebsiteGuid = null;
    // The typeahead below throws an invariant ("does not have a valid label string") and takes the whole
    // page down if ANY option's label is not a string, so a website role saved without a name has to get a
    // printable stand-in here rather than reach the menu as null.
    var websites = (state.user.websites || []).map((r) => {
      var name = typeof r.websiteName === 'string' && r.websiteName.trim()
        ? r.websiteName
        : 'Website ' + String(r.websiteGuid || '').substring(0, 8);
      var website = { guid: r.websiteGuid, label: name, isDefault: r.isDefault, websiteName: name };
      if (r.isDefault) {
        selectedWebsiteGuid = website.guid;
      }
      return website;
    });

    this.state = {
      redirect: null,
      selectedWebsiteGuid: selectedWebsiteGuid,
      websites: websites,
      isAuthenticated: false,
      chatEnabled: false,
      notificationEnabled: false,
      // attentionCount = customers waiting for a reply (badge); escalatedCount = waiting ≥ 2 min
      // (unanswered → flagged to the whole team so any available agent can pick the customer up).
      attentionCount: 0,
      escalatedCount: 0,
      // Support duty. `isSupportOperator` false = not on any roster, so the toggle is hidden entirely.
      // Defaults to available so a failed lookup never silently mutes a dispatcher who IS on duty.
      isSupportOperator: false,
      // Seeded from the local preference so a muted browser stays muted for the moment before the server
      // answers. For a roster member the server's answer replaces it immediately — it owns duty state.
      supportAvailable: readLocalSupportAvailable(),
      availabilitySaving: false,
      unattendedWebsites: [],
      unassignedCount: 0,
      myOpenCount: 0,
      notificationUnreadCount: null,
      notificationCountStatus: 'unknown'
    }

    // Chat unread badge plumbing
    this._chatCleanups = []
    this._notificationCleanups = []
    this._unreadDebounce = null
    this._pollInterval = null
    this._chatInited = false
    this._chatWebsiteGuid = null
    this._shownNotificationIds = new Set()
    //console.log('LayoutNavbar constructor', this.props.location.pathname , this.props.navigateToLink)
    if (this.props.navigateToLink && this.props.location.pathname === this.props.navigateToLink) {
      store.dispatch({ type: 'redirect', data: null })
    }
  }

  componentDidMount() {
    this.initChatUnread()
  }

  componentWillUnmount() {
    if (this._unreadDebounce) {
      clearTimeout(this._unreadDebounce)
      this._unreadDebounce = null
    }
    if (this._pollInterval) {
      clearInterval(this._pollInterval)
      this._pollInterval = null
    }
    // Remove our SignalR handlers, but leave the shared connection alive — the conversations
    // page uses the same singleton.
    this._chatCleanups.forEach((fn) => { try { fn() } catch { /* noop */ } })
    this._chatCleanups = []
    this._notificationCleanups.forEach((fn) => { try { fn() } catch { /* noop */ } })
    this._notificationCleanups = []
  }

  // Start shared support summary plumbing. Chat controls remain feature-gated, while notification
  // access is role-gated independently so notification delivery does not depend on chat being enabled.
  initChatUnread() {
    if (this._chatInited) return
    const selectedWebsite = getDomainInfo(this.props.user).selectedWebsite
    const websiteGuid = selectedWebsite?.websiteGuid
    const supportRoles = ['Administrator', 'Dispatcher', 'Agent', 'Support Operator']
    if (!websiteGuid || !hasAnyRole(selectedWebsite, supportRoles)) return

    this._chatInited = true
    this._chatWebsiteGuid = websiteGuid
    this.setState({ notificationEnabled: true })
    this.refreshUnread(websiteGuid)
    this.setupNotificationSignalR(websiteGuid)
    // Keep the existing summary owner/cadence; notification count comes only from this response.
    this._pollInterval = window.setInterval(() => this.refreshUnread(websiteGuid), 30000)
    getDashboardConfiguration(websiteGuid)
      .then((config) => {
        if (config?.enableChat) {
          this.setState({ chatEnabled: true })
          // Arm operator alerts only when chat is enabled; the notification badge remains independent.
          initChatSound()
          initChatDesktopNotifications()
          this.refreshSupportAvailability()
          this.setupChatSignalR(websiteGuid)
        }
      })
      .catch(() => { /* chat remains unavailable; notification summary still uses its existing poll */ })
  }

  // "I'm available for support". Off duty silences this browser's alerts AND drops the operator from the
  // escalation SMS roster — both read the same ChatAvailable flag, so there is one answer, not two.
  refreshSupportAvailability() {
    getSupportAvailability()
      .then(({ isOperator, available }) => this.setState({
        isSupportOperator: isOperator,
        // On the roster, the server owns this: it also decides escalation paging, so it must not be
        // second-guessed from a browser. Off the roster there is nothing server-side to own, and the
        // toggle is purely "alert me" — a per-browser preference.
        supportAvailable: isOperator ? available : readLocalSupportAvailable(),
      }))
      .catch(() => { /* keep alerts on rather than guessing someone is off duty */ })
  }

  toggleSupportAvailability = (e) => {
    e.preventDefault()
    if (this.state.availabilitySaving) return

    const next = !this.state.supportAvailable
    // Optimistic: the operator is telling us they have stopped taking chats, and the alert gate below must
    // respect that on the very next message rather than after a round trip.
    this.setState({ supportAvailable: next, availabilitySaving: true })
    writeLocalSupportAvailable(next)

    // Nothing to persist server-side for someone who is not on any operator roster — they have no
    // ChatAvailable row and are not in the escalation rotation. Their switch is the browser preference
    // written above, which is the whole of what it means for them.
    if (!this.state.isSupportOperator) {
      this.setState({ availabilitySaving: false })
      return
    }

    setSupportAvailability(next)
      .then(({ available, unattendedWebsites }) => this.setState({
        supportAvailable: available,
        availabilitySaving: false,
        // Non-empty only when this change left a website with nobody on duty. Escalation still pages the
        // whole roster as a last resort, so this is a heads-up, not a block — but the person who just
        // created the state is the one who can undo it in one click.
        unattendedWebsites: unattendedWebsites || [],
      }))
      .catch(() => this.setState({ supportAvailable: !next, availabilitySaving: false }))
  }

  refreshUnread(websiteGuid) {
    fetchConversationSummary(websiteGuid)
      .then((summary) => {
        // Team-wide "customer waiting for a reply" signal: waiting = any age (badge); unanswered =
        // waiting ≥ 2 min → escalate to everyone (pulse) so any available agent can pick it up.
        const waiting = summary?.attention?.waiting ?? 0
        const unanswered = summary?.attention?.unanswered ?? 0
        const notificationUnreadCount = summary?.notifications?.unreadCount ?? summary?.notification
        const notificationCountIsValid = typeof notificationUnreadCount === 'number' && Number.isFinite(notificationUnreadCount) && notificationUnreadCount >= 0
        this.setState((previousState) => ({
          attentionCount: waiting,
          escalatedCount: unanswered,
          unassignedCount: summary?.open?.unassigned ?? 0,
          myOpenCount: summary?.open?.my ?? 0,
          notificationUnreadCount: notificationCountIsValid
            ? notificationUnreadCount
            : previousState.notificationUnreadCount,
          notificationCountStatus: notificationCountIsValid ? 'valid' : 'unknown',
        }))
      })
      .catch(() => { /* keep last known count on transient error */ })
  }

  // Coalesce bursts of SignalR events into a single summary refetch.
  scheduleUnreadRefresh(websiteGuid) {
    if (this._unreadDebounce) clearTimeout(this._unreadDebounce)
    this._unreadDebounce = window.setTimeout(() => {
      this._unreadDebounce = null
      this.refreshUnread(websiteGuid)
    }, 500)
  }

  async setupChatSignalR(websiteGuid) {
    try {
      const conn = getChatSignalRConnection()
      await conn.start()
      await conn.joinWebsiteGroup(websiteGuid)

      const refresh = () => this.scheduleUnreadRefresh(websiteGuid)
      // Inbound customer/driver message → may raise unread; new conversation → new unread;
      // read-status change (operator read elsewhere) → may lower unread; resolve/reopen → re-sync.
      this._chatCleanups.push(conn.onMessage((message) => {
        if (message?.from === 'user' || message?.from === 'driver') {
          refresh()
          // The badge keeps counting whatever their duty state — it is information, not an interruption.
          // Only the nudge is silenced: chime + toast, and a desktop notification when the tab is
          // backgrounded.
          if (this.state.supportAvailable) notifyIncomingChat(message)
        }
      }))
      this._chatCleanups.push(conn.onNewConversation(refresh))
      this._chatCleanups.push(conn.onReadStatusUpdated(refresh))
      this._chatCleanups.push(conn.onConversationUpdated(refresh))
    } catch (err) {
      console.error('LayoutNavbar: failed to set up chat unread listener', err)
    }
  }

  async setupNotificationSignalR(websiteGuid) {
    try {
      const connection = getNotificationSignalRConnection()
      const currentUserId = String(store.getState().user?.userId || '').toLowerCase()
      const normalizeGuid = (value) => String(value || '').toLowerCase()

      await connection.start()
      this._notificationCleanups.push(connection.onNotificationCreated((event) => {
        if (event?.contractVersion !== 1 || !event?.notificationId || !event?.userId) return
        if (!currentUserId || normalizeGuid(event.userId) !== currentUserId) return
        if (event.websiteGuid && normalizeGuid(event.websiteGuid) !== normalizeGuid(websiteGuid)) return
        if (['SupplierRejectedBooking', 'DriverRejectedBooking'].includes(event.type) && !this._shownNotificationIds.has(event.notificationId)) {
          this._shownNotificationIds.add(event.notificationId)
          if (this._shownNotificationIds.size > 200) {
            this._shownNotificationIds.delete(this._shownNotificationIds.values().next().value)
          }
          showToastMessages(escapeToastText(event.title) || 'Supplier rejected a booking. Please reassign the supplier.', 'warning')
        }
        this.scheduleUnreadRefresh(websiteGuid)
      }))
      this._notificationCleanups.push(connection.onReconnected(() => {
        this.scheduleUnreadRefresh(websiteGuid)
      }))
    } catch (err) {
      console.error('LayoutNavbar: failed to set up notification listener', err)
    }
  }

  toggleSidenav(e) {
    e.preventDefault()
    layoutHelpers.toggleCollapsed()
  }

  logout = () => {
    localStorage.clear();
    store.dispatch({ type: 'setUserInfo', data: {} })
    //store.dispatch({type: 'redirect', data: '/login'})
    //this.setState({redirect: '/login'})
  }

  onChangeWebsite = (selectedWebsites) => {
    if (!selectedWebsites || selectedWebsites.length === 0) {
      return;
    }
    
    const websiteGuid = selectedWebsites[0].guid;
    
    // Only proceed if it's actually a different website
    if (websiteGuid === this.state.selectedWebsiteGuid) {
      return;
    }
    
    setDefaultWebsite(websiteGuid).then(data => {
      if (data) {
        getUserInfo().then(data => {
          store.dispatch({ type: 'setUserInfo', data: data });

          setTimeout(() => {
            window.location.reload();
          }, 500);
        })
      }
    });
  }

  render() {
    //console.log(this.props);
    if (!this.props.isLoggedIn) {
      return <Redirect to={'/login'} />
    }
    //console.log("title type", typeof(this.props.title));

    return (
      <Navbar bg={this.props.navbarBg} expand="sm" className="layout-navbar align-items-center container-p-x" style={{ zIndex: 1001 }}>

        {/* Brand */}
        {/* <Navbar.Brand as={NavLink} to="/">React Starter</Navbar.Brand> */}

        {/* Sidenav toggle */}
        {this.props.sidenavToggle && (
          <Nav className="align-items-lg-center mr-auto mr-lg-4">
            <Nav.Item as="a" className="nav-item nav-link px-0 ml-2 ml-lg-0" href="#toggle" onClick={this.toggleSidenav}>
              <i className="ion ion-md-menu text-large align-middle"></i>
            </Nav.Item>
          </Nav>
        )}

        {(this.props.title && typeof (this.props.title) == 'object')
          ? <div className="page-title" style={{ fontSize: '1.125rem', color: 'rgb(82, 79, 79)', fontWeight: 'bold', margin: 0 }}>{this.props.title.content}</div>
          : <div className="page-title" style={{ fontSize: '1.125rem', color: 'rgb(82, 79, 79)', fontWeight: 'bold', margin: 0 }}>{this.props.title}</div>
        }

        {/* Navbar toggle */}
        <Navbar.Toggle />

        <Navbar.Collapse>
          <Nav className="align-items-lg-center ml-auto">
            {/* Shown to everyone who RECEIVES the alerts, not only to roster members: the sidenav and this
                navbar gate chat on roles, so an Administrator who is not an operator gets every chime and
                would otherwise have no way to stop them. For them it is an alert preference; for a roster
                member it additionally takes them out of escalation paging. */}
            {this.state.chatEnabled && (
              <Nav.Item className="mr-2">
                <a
                  href="#support-availability"
                  onClick={this.toggleSupportAvailability}
                  className="nav-link px-2 d-inline-flex align-items-center"
                  style={{ gap: 6, opacity: this.state.availabilitySaving ? 0.6 : 1 }}
                  title={this.state.supportAvailable
                    ? (this.state.isSupportOperator
                      ? 'You are on duty and receiving support alerts. Click to go off duty — you will stop getting message alerts and escalation texts.'
                      : 'You are receiving support alerts in this browser. Click to silence them.')
                    : (this.state.isSupportOperator
                      ? 'You are off duty. You are not being alerted about new messages, and escalation texts go to the other operators. Click to come back on duty.'
                      : 'Support alerts are silenced in this browser. Click to turn them back on.')}
                >
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      display: 'inline-block',
                      background: this.state.supportAvailable ? '#28a745' : '#adb5bd',
                    }}
                  />
                  <span style={{ fontSize: 12 }}>
                    {this.state.supportAvailable ? 'Available' : 'Away'}
                  </span>
                </a>
              </Nav.Item>
            )}
            {/* Only after this operator's own switch-off left a roster empty. Escalation still pages everyone
                as a last resort, so this is a heads-up rather than an error — and they can undo it in one
                click, which is why it is shown here and not buried in a settings page. */}
            {this.state.isSupportOperator && !this.state.supportAvailable && this.state.unattendedWebsites.length > 0 && (
              <Nav.Item className="mr-2">
                <span
                  className="text-warning d-inline-flex align-items-center"
                  style={{ fontSize: 12 }}
                  title={`Nobody is marked available for ${this.state.unattendedWebsites.join(', ')}. New messages will still escalate by SMS to the whole team, but nobody is watching the screen.`}
                >
                  ⚠ Nobody on duty
                </span>
              </Nav.Item>
            )}
            {this.state.chatEnabled && (
              <Dropdown as={Nav.Item} alignRight={!this.isRTL} className="cbs-chat-dd mr-2">
                <Dropdown.Toggle
                  as={Nav.Link}
                  className={`cbs-chat-toggle cbs-chat-nav px-2 position-relative${this.state.escalatedCount > 0 ? ' cbs-chat-nav--alert' : ''}`}
                  title={
                    this.state.attentionCount > 0
                      ? `${this.state.attentionCount} chat${this.state.attentionCount === 1 ? '' : 's'} waiting for a reply` +
                        (this.state.escalatedCount > 0 ? ` — ${this.state.escalatedCount} over 2 min, please pick up` : '')
                      : this.props.t('common:navbar.conversations', { defaultValue: 'Support conversations' })
                  }
                  style={{ display: 'inline-flex', alignItems: 'center' }}
                >
                  <i className="ion ion-ios-chatboxes cbs-chat-nav__icon" style={{ fontSize: '1.35rem', lineHeight: 1 }}></i>
                  {this.state.attentionCount > 0 && (
                    <span className="cbs-chat-nav__badge">
                      {this.state.attentionCount > 99 ? '99+' : this.state.attentionCount}
                    </span>
                  )}
                </Dropdown.Toggle>

                <Dropdown.Menu className="cbs-chat-menu">
                  <div className="cbs-chat-menu__title">Support chats</div>
                  {(this.state.attentionCount + this.state.unassignedCount) === 0 ? (
                    <div className="cbs-chat-menu__empty">All caught up ✓</div>
                  ) : (
                    <>
                      {this.state.escalatedCount > 0 && (
                        <div className="cbs-chat-menu__row cbs-chat-menu__row--alert">
                          <span>Unanswered over 2 min</span>
                          <span className="cbs-chat-menu__count">{this.state.escalatedCount}</span>
                        </div>
                      )}
                      <div className="cbs-chat-menu__row">
                        <span>Unassigned</span>
                        <span className="cbs-chat-menu__count">{this.state.unassignedCount}</span>
                      </div>
                      <div className="cbs-chat-menu__row">
                        <span>Waiting for a reply</span>
                        <span className="cbs-chat-menu__count">{this.state.attentionCount}</span>
                      </div>
                      <div className="cbs-chat-menu__row">
                        <span>Assigned to me</span>
                        <span className="cbs-chat-menu__count">{this.state.myOpenCount}</span>
                      </div>
                    </>
                  )}
                  <Dropdown.Divider />
                  <Dropdown.Item href="#/support/conversations" className="cbs-chat-menu__open">
                    Open conversations →
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            )}

            {this.state.notificationEnabled && this._chatWebsiteGuid && (
              <Nav.Item className="mr-2">
                <NotificationBell
                  websiteGuid={this._chatWebsiteGuid}
                  unreadCount={this.state.notificationUnreadCount}
                  onUnreadCountChange={(count) => this.setState({ notificationUnreadCount: count, notificationCountStatus: 'valid' })}
                />
              </Nav.Item>
            )}

            {this.state.websites.length > 1 && (
              <div style={{ minWidth: '250px', maxWidth: '400px' }} className="mr-3">
                <Typeahead
                  id="website-selector"
                  options={this.state.websites}
                  labelKey="websiteName"
                  defaultSelected={this.state.websites.filter(w => w.guid === this.state.selectedWebsiteGuid)}
                  onChange={this.onChangeWebsite}
                  placeholder="Select website..."
                  clearButton
                  size="sm"
                />
              </div>
            )}

            <Dropdown as={Nav.Item} className="demo-navbar-user" alignRight={!this.isRTL}>
              <Dropdown.Toggle as={Nav.Link}>
                <span className="d-inline-flex flex-lg-row-reverse align-items-center align-middle">
                  <img src={`${process.env.PUBLIC_URL}/login-logo.png`} className="d-block ui-w-30 rounded-circle" alt="User" />
                  <span className="px-1 mr-lg-2 ml-2 ml-lg-0">{this.props.user.firstName} {this.props.user.lastName}</span>
                </span>
              </Dropdown.Toggle>

              <Dropdown.Menu>
                <Dropdown.Item href="#/my-profile" id="myProfileLink">
                  <i className="ion ion-ios-person text-lightest"></i> &nbsp; {this.props.t('common:navbar.myProfile')}
                </Dropdown.Item>
                {/* <Dropdown.Item hred="#"><i className="ion ion-ios-mail text-lightest"></i> &nbsp; Messages</Dropdown.Item> */}
                {/* <Dropdown.Item hred="#"><i className="ion ion-md-settings text-lightest"></i> &nbsp; Account settings</Dropdown.Item> */}
                <Dropdown.Divider />
                <Dropdown.Item hred="#" onClick={this.logout}><i className="ion ion-ios-log-out text-danger"></i> &nbsp; {this.props.t('common:navbar.logout')}</Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Nav>
        </Navbar.Collapse>
      </Navbar>
    )
  }
}

LayoutNavbar.propTypes = {
  sidenavToggle: PropTypes.bool
}

LayoutNavbar.defaultProps = {
  sidenavToggle: true
}

export default withTranslation()(connect(store => ({
  navbarBg: store.theme.navbarBg,
  isLoggedIn: store.user?.token != null,
  user: store.user
}))(LayoutNavbar))
